import { Badge, Box, Button, HoverCard, Select, Tabs, Text } from '@mantine/core';
import { IconAlertCircle, IconListDetails, IconSparkles, IconCheck } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useAwareZindex } from '~/hooks/context/useZIndex';
import Link from 'next/link';
import type { AiChange, TestRunExecutionOutput } from '~/types';

type AiChangeWithAttempt = AiChange & { attemptId: string; attemptIndex: number };

export default function AiChangeQuickView({
  aiChanges,
  runId,
  errors,
}: {
  aiChanges: AiChangeWithAttempt[];
  runId: string;
  errors: TestRunExecutionOutput['errors'];
}) {
  const router = useRouter();
  const projectId = useProjectId();
  const zIndexBase = useAwareZindex();

  const aiChangesByAttempt = useMemo(() => {
    return Object.groupBy(aiChanges, (aiChange) => aiChange.attemptId);
  }, [aiChanges.length]);

  const attemptOptions = useMemo(() => {
    return Object.keys(aiChangesByAttempt).map((attemptId) => {
      const aiChangeWithIndex = aiChanges.find((aiChange) => aiChange.attemptId === attemptId);
      const attemptIndex = aiChangeWithIndex?.attemptIndex;

      return {
        value: attemptId,
        label: `Attempt #${attemptIndex || 1}`,
      };
    });
  }, [aiChangesByAttempt]);

  const [selectedAttemptId, setSelectedAttemptId] = useState<string>(attemptOptions.at(0)?.value || '');

  const selectedAiChanges = aiChangesByAttempt[selectedAttemptId] || [];
  const successCount = useMemo(
    () => selectedAiChanges.filter((aiChange) => aiChange.isSuccess).length,
    [selectedAiChanges],
  );

  if (aiChanges.length === 0) {
    return null;
  }

  const renderErroredOperation = (aiChange: AiChange) => {
    if (!aiChange.erroredOperationId) {
      return undefined;
    }

    const erroredOperation = errors.find((error) => error.operationId === aiChange.erroredOperationId);

    return (
      <>
        <Text
          size="sm"
          c="dimmed"
        >
          Encountered an error {!!erroredOperation && `while trying to ${erroredOperation.operationDescription}`}
        </Text>
        <Text
          size="sm"
          className="rounded border-l-2 border-red-500 bg-red-50 p-2 break-words whitespace-pre-wrap dark:bg-red-950/20"
          c="red"
        >
          {erroredOperation?.error}
        </Text>
      </>
    );
  };

  const renderSingleChange = (aiChange: AiChangeWithAttempt) => (
    <Box className="space-y-3">
      {/* Step Information */}
      <div className="flex items-start gap-3 pt-1">
        <div>
          <div className="mb-1 items-center gap-2">
            <Text
              size="sm"
              fw={500}
              c="dimmed"
            >
              Auto-healed {aiChange.stepIndex !== undefined ? `Step ${aiChange.stepIndex + 1}` : 'Step'}:{' '}
              {aiChange.stepDescription}
            </Text>
          </div>
        </div>
      </div>
      {/* Errored Action */}
      {renderErroredOperation(aiChange)}

      {/* Change Details */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {aiChange.isSuccess ?
            <IconCheck
              size={16}
              className="flex-shrink-0 text-green-500"
            />
          : <IconAlertCircle
              size={16}
              className="flex-shrink-0 text-red-500"
            />
          }
          <Text
            size="sm"
            fw={500}
            c={aiChange.isSuccess ? 'green' : 'red'}
          >
            {aiChange.isSuccess ? 'Successfully healed:' : 'Healing failed:'}
          </Text>
        </div>
        <Text
          size="sm"
          className={`rounded border-l-2 p-2 whitespace-pre-wrap ${
            aiChange.isSuccess ?
              'border-green-500 bg-green-50 dark:bg-green-950/20'
            : 'border-red-500 bg-red-50 dark:bg-red-950/20'
          }`}
          c={aiChange.isSuccess ? 'green' : 'red'}
        >
          {aiChange.summary}
        </Text>
      </div>
    </Box>
  );

  const renderMultipleChanges = () => (
    <Tabs
      defaultValue="0"
      className="flex h-full flex-col"
    >
      <Tabs.List className="flex-shrink-0">
        {selectedAiChanges.map((aiChange, index) => (
          <Tabs.Tab
            key={index}
            value={index.toString()}
            leftSection={aiChange.isSuccess ? <IconCheck size={14} /> : <IconAlertCircle size={14} />}
          >
            {aiChange.stepIndex !== undefined ? `Step ${aiChange.stepIndex + 1}` : `Change ${index + 1}`}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {selectedAiChanges.map((aiChange, index) => (
          <Tabs.Panel
            key={index}
            value={index.toString()}
            pt="md"
          >
            {renderSingleChange(aiChange)}
          </Tabs.Panel>
        ))}
      </div>
    </Tabs>
  );

  return (
    <HoverCard
      width={420}
      shadow="md"
      withArrow
      openDelay={300}
      closeDelay={400}
      zIndex={zIndexBase + 30}
    >
      <HoverCard.Target>
        <Link
          href={{
            pathname: router.pathname,
            query: { ...router.query, testRunDrawer: runId },
          }}
          as={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${runId}`}
          className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-md bg-violet-500/20 px-2 py-1 text-xs font-medium transition-colors hover:bg-violet-500/30 dark:text-white"
        >
          <IconSparkles size={12} />
          {aiChanges.length} Auto-heal{aiChanges.length > 1 ? `s` : ''}
        </Link>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <div className="flex max-h-[calc(100vh-200px)] flex-col space-y-3">
          <div className="flex-shrink-0">
            <Text
              size="sm"
              fw={500}
            >
              {successCount === selectedAiChanges.length ?
                'Auto-healed all steps'
              : successCount > 0 ?
                'Auto-healed some steps'
              : 'Auto-heal was not successful'}
            </Text>
          </div>

          {/* Attempt Selector */}
          {attemptOptions.length > 1 && (
            <div className="flex-shrink-0">
              <Select
                data={attemptOptions}
                value={selectedAttemptId}
                defaultValue={attemptOptions[0].value}
                onChange={(value) => setSelectedAttemptId(value || 'unknown')}
                placeholder="Select attempt"
                allowDeselect={false}
                renderOption={({ option, checked }) => (
                  <div className="flex items-center gap-2">
                    <IconCheck
                      className={!checked ? 'invisible' : ''}
                      size={14}
                    />
                    <Text size="sm">{option.label}</Text>
                    <Badge
                      size="xs"
                      color="violet"
                      variant="light"
                    >
                      {aiChangesByAttempt[option.value]?.length || 0}{' '}
                      {aiChangesByAttempt[option.value]?.length === 1 ? 'change' : 'changes'}
                    </Badge>
                  </div>
                )}
              />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {selectedAiChanges.length > 0 ?
              selectedAiChanges.length === 1 ?
                renderSingleChange(selectedAiChanges[0])
              : renderMultipleChanges()
            : <Text
                size="sm"
                c="dimmed"
              >
                No AI change details available for this attempt
              </Text>
            }
          </div>

          {selectedAiChanges.length > 0 && (
            <div className="flex-shrink-0">
              <Button
                variant="light"
                size="xs"
                component={Link}
                href={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    testRunDrawer: runId,
                    ...(selectedAttemptId !== 'unknown' && { attemptId: selectedAttemptId }),
                  },
                }}
                as={`/project/${projectId}/history/${router.query.runOrSuiteId}/run/${runId}`}
                fullWidth
                leftSection={<IconListDetails size={14} />}
              >
                View Full Details
              </Button>
            </div>
          )}
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
